/**
 * PdwFooterDyad — canonical [← Back] / [Continue →] sticky-footer dyad for
 * PersonDialog shells (BACKLOG-0016).
 *
 * Replaces the older Cancel + save-button footer with the bank-wide locked
 * convention (Bill 2026-05-11, navigation lock):
 *
 *   - [← Back]   left-aligned, ghost. Disabled on the first stage.
 *   - [Continue →] right-aligned, primary (PCB blue). Disabled until the
 *                  current stage's validation passes.
 *
 * On the final stage the Continue label flips to whatever the consumer passes
 * via `lastStepLabel` — most callers use "Save & Close →", but the Sole Prop
 * cart→sign handoff uses "Sign →".
 *
 * Mirrors the visual treatment of CartDetail's sticky footer (RAO record page).
 */

import type { CSSProperties } from 'react';
import { Button } from '@/components/primitives';

export interface PdwFooterDyadProps {
  /** Zero-based index of the current stage/tab. */
  currentStep: number;
  /** Total number of stages/tabs. Pass 1 for single-stage dialogs. */
  totalSteps: number;
  /** Called when Return is clicked. Not invoked when disabled. */
  onReturn: () => void;
  /** Called when Continue is clicked (advance OR save on final stage). */
  onContinue: () => void;
  /** Overrides Return-disabled (e.g. when the very first stage is also gated). */
  returnDisabled?: boolean;
  /** Disables Continue (validation failures, supervisor gate, etc.). */
  continueDisabled?: boolean;
  /** Tooltip surfaced on Continue when disabled. */
  continueDisabledReason?: string;
  /**
   * Label rendered on Continue when on the final stage. Default 'Save & Close →'.
   * Pass 'Sign →' for cart-context handoff per the navigation lock.
   */
  lastStepLabel?: string;
  /** Non-final-stage Continue label. Default 'Continue →'. */
  continueLabel?: string;
  /** Custom Return label (default '← Back'). */
  returnLabel?: string;
}

const footerStyle: CSSProperties = {
  position: 'sticky',
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-4)',
  height: 56,
  padding: '0 var(--sp-5)',
  margin: '0 calc(-1 * var(--sp-8)) calc(-1 * var(--sp-8))',
  background: 'var(--surface-card, #fff)',
  borderTop: '1px solid var(--ink-200)',
  boxShadow: '0 -2px 6px rgba(26, 64, 106, 0.06)',
  zIndex: 1,
};

export function PdwFooterDyad({
  currentStep,
  totalSteps,
  onReturn,
  onContinue,
  returnDisabled,
  continueDisabled,
  continueDisabledReason,
  lastStepLabel = 'Save & Close →',
  continueLabel = 'Continue →',
  returnLabel = '← Back',
}: PdwFooterDyadProps): JSX.Element {
  const isFirst = currentStep <= 0;
  const isLast = currentStep >= totalSteps - 1;
  const returnIsDisabled = returnDisabled ?? isFirst;

  return (
    <footer style={footerStyle} className="pdw-footer-dyad">
      <Button
        variant="ghost"
        onClick={onReturn}
        disabled={returnIsDisabled}
        title={returnIsDisabled ? 'Already on the first stage' : undefined}
      >
        {returnLabel}
      </Button>
      <div style={{ flex: 1 }} />
      <Button
        variant="primary"
        onClick={onContinue}
        disabled={continueDisabled}
        title={continueDisabledReason}
      >
        {isLast ? lastStepLabel : continueLabel}
      </Button>
    </footer>
  );
}
